import styled from '@emotion/styled'
import { ListItemIcon, ListItemText, Menu as MuiMenu, MenuItem as MuiMenuItem } from '@mui/material'
import { memo } from 'react'

import { colorTokens } from '../../foundations/color-tokens.ts'
import { borderRadius, borderStroke, Text } from '../../foundations/index.ts'
import { shadow } from '../../foundations/shadow-tokens.ts'
import { spacing } from '../../foundations/spacing-tokens.ts'
import { Avatar } from '../avatar/index.ts'
import { Divider } from '../divider/divider.tsx'

import type { MenuItem } from './menu.tsx'

type FloatingMenuListSize = 'small' | 'medium' | 'large'

type FloatingMenuListProps = {
  menuItems: MenuItem[]
  handleClose: () => void
  open: boolean
  onChange: (value: string) => void
  anchorElement: HTMLElement | null
  buttonWidth: number
  position: 'left' | 'right'
  size: FloatingMenuListSize
}

export const FloatingMenuList = memo<FloatingMenuListProps>(function MenuList(props) {
  const { menuItems, anchorElement, open, handleClose, onChange, buttonWidth, position, size } = props

  return (
    <StyledMenu
      aria-labelledby="navigation-menu"
      anchorEl={anchorElement}
      open={open}
      onClose={handleClose}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: position,
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: position,
      }}
      buttonWidth={buttonWidth}
      size={size}
    >
      {menuItems.map((item, index) => (
        <div key={'divider' in item ? `divider-${index}` : item.value}>
          {'divider' in item ? (
            <Divider orientation="horizontal" />
          ) : (
            <MuiMenuItem>
              {item.icon ? (
                <ListItemIcon>
                  <Avatar icon={item.icon} size="30" />
                </ListItemIcon>
              ) : null}
              <ListItemText onClick={() => onChange(item.value)}>
                <Text>{item.label}</Text>
              </ListItemText>
            </MuiMenuItem>
          )}
        </div>
      ))}
    </StyledMenu>
  )
})

const StyledMenu = styled(MuiMenu, {
  shouldForwardProp: (propName) => !['buttonWidth'].includes(propName),
})<{ buttonWidth: number; size: FloatingMenuListSize }>`
  .MuiPaper-root {
    margin-top: ${spacing[30]};
    border-radius: ${borderRadius['20']};
    border: ${borderStroke[20]} solid ${colorTokens['color-stroke-base-main']};
    background: ${colorTokens['color-bg-base-normal']};
    min-width: ${({ buttonWidth }) => buttonWidth}px;
    box-shadow: ${shadow.bottom['30']};
  }

  .MuiList-root {
    padding: 0;
  }

  .MuiMenuItem-root {
    padding: ${({ size }) => paddingPerMenuSize[size]};
  }
`

const paddingPerMenuSize: Record<FloatingMenuListSize, string> = {
  small: `${spacing[30]} ${spacing[50]}`,
  medium: `${spacing[40]} ${spacing[50]}`,
  large: `${spacing[50]} ${spacing[50]}`,
}
